import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MakeChangeActions } from '../actions/cir.actions';
import { from, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { computed, inject, Injectable, signal } from '@angular/core';
import { CirSetupService } from '../services/cir-setup.service';
import { Store } from '@ngrx/store';
import {
  selectReviewLocationType,
  selectStudyId,
  selectWorkspaceType,
} from '../selectors/sites.selector';
import { defaultPageOptions } from '@shared/constants';
import {
  RegionType,
  SearchParams,
  SiteSearchFunction,
} from '../type/setup.type';

@Injectable()
export class MakeChangeEffects {
  private cirSetupService = inject(CirSetupService);

  $field = signal('');
  $page = signal(defaultPageOptions().pageIndex);
  $pageSize = signal(defaultPageOptions().pageSize);

  $defaultParams = computed<SearchParams>(() => ({
    field: this.$field(),
    page: this.$page(),
    pageSize: this.$pageSize(),
  }));

  private actions$ = inject(Actions);
  private _store = inject(Store);
  private siteSearchCallbacks = signal<Record<string, SiteSearchFunction<any>>>(
    {
      default: (study, params = this.$defaultParams()) =>
        this.cirSetupService.getSitesStudySearch(
          study,
          params.page,
          params.pageSize,
          params.field,
        ),
      multiSiteRegion: (study, params = this.$defaultParams()) =>
        this.cirSetupService.getSitesForStudyByRegion(
          study,
          params.page,
          params.pageSize,
          params.field,
          params.region,
        ),
    },
  );
  loadSitesOnStudyChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MakeChangeActions.storeStudyId,
        MakeChangeActions.storeReviewLocationType,
      ),
      withLatestFrom(
        this._store.select(selectWorkspaceType),
        this._store.select(selectStudyId),
        this._store.select(selectReviewLocationType),
      ),
      switchMap(([_, workspaceType, studyId, reviewLocationType]) => {
        const params = {
          ...this.$defaultParams(),
          region: reviewLocationType as RegionType,
        };
        let searchCallback = this.siteSearchCallbacks()['default'];

        if (
          workspaceType?.data.select_multi_site &&
          workspaceType?.data.select_region
        ) {
          searchCallback = this.siteSearchCallbacks()['multiSiteRegion'];
        }

        const shouldStoreMultiSite =
          params.region.trim() === '' && params.field.trim() === '';
        if (shouldStoreMultiSite) {
          MakeChangeActions.setSiteLoaded({
            siteLoaded: false,
          });
        }
        return searchCallback(studyId, params).pipe(
          map((sites) => {
            const total = sites.total || 0;

            if (shouldStoreMultiSite) {
              return [
                MakeChangeActions.loadSitesSuccess({ sites }),
                MakeChangeActions.setTotalSites({
                  totalSites: total,
                }),
                MakeChangeActions.setSiteLoaded({
                  siteLoaded: true,
                }),
              ];
            }

            return MakeChangeActions.loadSitesSuccess({ sites });
          }),
          switchMap((actions) =>
            Array.isArray(actions) ? from(actions) : of(actions),
          ),
        );
      }),
    ),
  );
  loadSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MakeChangeActions.loadSites),
      withLatestFrom(
        this._store.select(selectWorkspaceType),
        this._store.select(selectStudyId),
        this._store.select(selectReviewLocationType),
      ),
      switchMap(
        ([
          { pageIndex, pageSize, field },
          workspaceType,
          studyId,
          reviewLocationType,
        ]) => {
          const params = {
            field,
            page: pageIndex,
            pageSize,
            region: reviewLocationType as RegionType,
          };
          this.$field.set(params.field);
          this.$page.set(params.page);
          this.$pageSize.set(params.pageSize);

          let searchCallback = this.siteSearchCallbacks()['default'];

          if (
            workspaceType?.data.select_multi_site &&
            workspaceType?.data.select_region
          ) {
            searchCallback = this.siteSearchCallbacks()['multiSiteRegion'];
          }

          return searchCallback(studyId, params).pipe(
            map((sites) => MakeChangeActions.loadSitesSuccess({ sites })),
          );
        },
      ),
    ),
  );
  loadStoreStudyName$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MakeChangeActions.storeStudyName),
        tap(() => {
          this.$field.set('');
          this.$page.set(defaultPageOptions().pageIndex);
          this.$pageSize.set(defaultPageOptions().pageSize);
        }),
      ),
    { dispatch: false },
  );
}
